"use client";
import { InfoAlert } from "@components/CustomAlert";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHasMounted } from "@core/hooks/useHasMounted";
import {
  getMessaging,
  isSupported,
  onMessage,
  MessagePayload,
} from "firebase/messaging";
import { app } from "../app";

export const validateMessaging = async () => {
  const supported = await isSupported();
  if (supported) return getMessaging(app);
  return;
};

export const Messaging = () => {
  const [newPayload, setPayload] = useState<MessagePayload | undefined>();
  const hasMounted = useHasMounted();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  useEffect(() => {
    const unlockAudio = () => {
      audioRef.current = new Audio("/sounds/AlertAudio.mp3");

      // reproducir una vez en silencio para desbloquear
      audioRef.current
        .play()
        .then(() => {
          audioRef.current!.pause();
          audioRef.current!.currentTime = 0;
          setAudioUnlocked(true);
        })
        .catch(() => {});

      document.removeEventListener("click", unlockAudio);
    };

    document.addEventListener("click", unlockAudio);

    return () => {
      document.removeEventListener("click", unlockAudio);
    };
  }, []);

  const playSound = useCallback(() => {
    if (!audioUnlocked || !audioRef.current) return;

    audioRef.current.currentTime = 0;
    audioRef.current.play().catch((e) => console.log("Audio error", e));
  }, [audioUnlocked]);

  // 📩 Escuchar mensajes en foreground
  useEffect(() => {
    const listenMessage = async () => {
      try {
        const messaging = await validateMessaging();
        if (!messaging) return;

        return onMessage(messaging, (payload) => {
          setPayload(payload);
        });
      } catch (error) {
        console.log("ERROR getting message", error);
      }
    };

    listenMessage();
  }, []);

  // 🔔 Reaccionar al mensaje
  useEffect(() => {
    if (!newPayload) return;

    playSound();
    InfoAlert(
      newPayload.notification?.title ?? "",
      newPayload.notification?.body,
    );

    setPayload(undefined);
  }, [newPayload, playSound]);

  // 🔁 Mensajes desde Service Worker
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.onmessage = (event) => {
        if (event.data?.type === "PLAY_SOUND") {
          playSound();
        }
      };
    }
  }, [audioUnlocked, playSound]);

  if (!hasMounted) return null;
  return null;
};
